import { create } from "zustand";

interface SummaryGenerationState {
	// Session ids whose close-time AI summary is being generated in the background.
	generatingSessionIds: Record<string, boolean>;
	// Session ids whose summary has already resolved (TextSummaryReady received). Tracked so the
	// wrap-up effect can't re-arm "generating" on a remount after the event already fired - which
	// would otherwise leave the spinner stuck until wrap-up ends.
	resolvedSessionIds: Record<string, boolean>;
}

interface SummaryGenerationActions {
	setGenerating: (sessionId: string) => void;
	clearGenerating: (sessionId: string) => void;
	// Drops a session from both maps when wrap-up ends, so ids don't accumulate.
	stopTracking: (sessionId: string) => void;
}

export const useSummaryGenerationStore = create<
	SummaryGenerationState & SummaryGenerationActions
>((set) => ({
	generatingSessionIds: {},
	resolvedSessionIds: {},
	setGenerating: (sessionId: string) =>
		set((state) => {
			// Don't (re)arm once the summary has resolved, or if it's already flagged.
			if (
				state.resolvedSessionIds[sessionId] ||
				state.generatingSessionIds[sessionId]
			) {
				return state;
			}

			return {
				generatingSessionIds: {
					...state.generatingSessionIds,
					[sessionId]: true,
				},
			};
		}),
	clearGenerating: (sessionId: string) =>
		set((state) => {
			if (
				!state.generatingSessionIds[sessionId] &&
				state.resolvedSessionIds[sessionId]
			) {
				return state;
			}

			const nextGenerating = { ...state.generatingSessionIds };
			delete nextGenerating[sessionId];

			return {
				generatingSessionIds: nextGenerating,
				resolvedSessionIds: { ...state.resolvedSessionIds, [sessionId]: true },
			};
		}),
	stopTracking: (sessionId: string) =>
		set((state) => {
			if (
				!state.generatingSessionIds[sessionId] &&
				!state.resolvedSessionIds[sessionId]
			) {
				return state;
			}

			const nextGenerating = { ...state.generatingSessionIds };
			const nextResolved = { ...state.resolvedSessionIds };
			delete nextGenerating[sessionId];
			delete nextResolved[sessionId];

			return {
				generatingSessionIds: nextGenerating,
				resolvedSessionIds: nextResolved,
			};
		}),
}));

export const useIsSummaryGenerating = (sessionId?: string): boolean =>
	useSummaryGenerationStore((state) =>
		sessionId ? !!state.generatingSessionIds[sessionId] : false
	);
